// Central business NAP + profile info for JSON-LD structured data (local SEO).
export const BUSINESS = {
  name: 'A Quality Handyman LLC',
  url: 'https://aqualityhandyman.com',
  telephone: '+1-505-219-7816',
  email: 'admin@aqualityhandyman.com',
  addressLocality: 'Albuquerque',
  addressRegion: 'NM',
  addressCountry: 'US',
  priceRange: '$$',
  image: 'https://aqualityhandyman.com/mascot-transparent.png',
  logo: 'https://aqualityhandyman.com/mascot-transparent.png',
  description: 'A Quality Handyman LLC is a locally owned handyman and home-improvement company serving Albuquerque, New Mexico and surrounding communities. Residential and commercial repairs, remodeling, interior and exterior painting, stucco, drywall, flooring, doors, plumbing and electrical repairs, landscaping and property maintenance. Service-area business (no public storefront). Free estimates by phone or online.',
  // Google Business/Maps listing (owner-provided). Modeled as hasMap, not sameAs.
  hasMap: 'https://maps.app.goo.gl/UGNFuEhupLGgjFpE8',
  // Only exact, owner-verified business-profile URLs belong here. Pending owner confirmation (2026-09).
  sameAs: [],
};

export const SERVICE_AREAS = [
  'Albuquerque',
  'Rio Rancho',
  'Santa Fe',
  'Corrales',
  'Bernalillo',
  'Los Lunas',
  'Placitas',
];

// LocalBusiness provider node reused inside Service schemas.
export const localBusinessNode = () => ({
  '@type': 'HomeAndConstructionBusiness',
  '@id': `${BUSINESS.url}/#business`,
  name: BUSINESS.name,
  url: BUSINESS.url,
  telephone: BUSINESS.telephone,
  email: BUSINESS.email,
  image: BUSINESS.image,
  priceRange: BUSINESS.priceRange,
  address: {
    '@type': 'PostalAddress',
    addressLocality: BUSINESS.addressLocality,
    addressRegion: BUSINESS.addressRegion,
    addressCountry: BUSINESS.addressCountry,
  },
  areaServed: SERVICE_AREAS.map((c) => ({ '@type': 'City', name: `${c}, NM` })),
  hasMap: BUSINESS.hasMap,
  ...(BUSINESS.sameAs.length ? { sameAs: BUSINESS.sameAs } : {}),
  parentOrganization: { '@id': `${BUSINESS.url}/#organization` },
});

// Organization node (site-wide identity; linked from LocalBusiness via parentOrganization).
export const organizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BUSINESS.url}/#organization`,
  name: BUSINESS.name,
  legalName: BUSINESS.name,
  url: BUSINESS.url,
  logo: { '@type': 'ImageObject', url: BUSINESS.logo },
  email: BUSINESS.email,
  telephone: BUSINESS.telephone,
  description: BUSINESS.description,
  areaServed: SERVICE_AREAS.map((c) => ({ '@type': 'City', name: `${c}, NM` })),
  contactPoint: [{ '@type': 'ContactPoint', telephone: BUSINESS.telephone, email: BUSINESS.email, contactType: 'customer service', areaServed: 'US-NM', availableLanguage: ['English'] }],
  ...(BUSINESS.sameAs.length ? { sameAs: BUSINESS.sameAs } : {}),
});

// WebSite node.
export const websiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${BUSINESS.url}/#website`,
  name: BUSINESS.name,
  url: BUSINESS.url,
  publisher: { '@id': `${BUSINESS.url}/#organization` },
  inLanguage: 'en-US',
});

// Service schema for a specific service (used on service pages).
export const serviceJsonLd = ({ name, description, path, serviceType }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType: serviceType || name,
  description,
  url: `${BUSINESS.url}${path}`,
  provider: localBusinessNode(),
  areaServed: SERVICE_AREAS.map((c) => ({ '@type': 'City', name: `${c}, NM` })),
});

// LocalBusiness schema scoped to a specific city (used on service-area pages).
export const cityBusinessJsonLd = ({ city, path, description }) => ({
  '@context': 'https://schema.org',
  '@type': 'HomeAndConstructionBusiness',
  name: `${BUSINESS.name} — ${city}`,
  description,
  url: `${BUSINESS.url}${path}`,
  telephone: BUSINESS.telephone,
  email: BUSINESS.email,
  image: BUSINESS.image,
  priceRange: BUSINESS.priceRange,
  address: {
    '@type': 'PostalAddress',
    addressLocality: BUSINESS.addressLocality,
    addressRegion: BUSINESS.addressRegion,
    addressCountry: BUSINESS.addressCountry,
  },
  areaServed: { '@type': 'City', name: `${city}, NM` },
  hasMap: BUSINESS.hasMap,
  parentOrganization: { '@id': `${BUSINESS.url}/#organization` },
});

// FAQPage schema from an array of {q, a}.
export const faqJsonLd = (faqs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

// BreadcrumbList schema from an array of {name, path} (local SEO — shows a clickable trail in search results).
export const breadcrumbJsonLd = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${BUSINESS.url}${it.path}`,
  })),
});

// Homepage-only ItemList of core services (each item carries its absolute page URL so validators accept it).
export const SERVICE_LINKS = [
  ['Flooring Installation', '/flooring'], ['Interior Painting', '/interior-painting'], ['Exterior Painting', '/exterior-painting'], ['Stucco Repair & Painting', '/stucco'],
  ['Drywall Repair & Installation', '/drywall'], ['Home Remodeling', '/remodeling'], ['Bathroom Remodeling', '/bathroom-remodeling'], ['Door Replacement', '/door-replacement'],
  ['Carpentry', '/carpentry'], ['Plumbing Repairs', '/plumbing'], ['Electrical Repairs', '/electrical'], ['General Repairs', '/general-repairs'],
  ['Landscaping & Xeriscaping', '/landscaping'], ['Hauling & Dump Trailer', '/hauling'], ['Material Delivery', '/material-delivery'], ['Faux Finishes', '/faux-finishes'],
  ['Commercial Handyman', '/commercial-handyman'], ['Building Maintenance', '/building-maintenance'], ['Facility Maintenance', '/facility-maintenance'], ['Commercial Property Maintenance', '/commercial-property-maintenance'],
];
export const servicesItemListJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'A Quality Handyman Services',
  itemListOrder: 'https://schema.org/ItemListUnordered',
  numberOfItems: SERVICE_LINKS.length,
  itemListElement: SERVICE_LINKS.map(([name, path], i) => ({ '@type': 'ListItem', position: i + 1, name, url: `${BUSINESS.url}${path}` })),
});
